import type { Map as ImmutableMap } from 'immutable';
import * as React from 'react';
import { memo, useRef } from 'react';

import type { accountDiscoveryTypes } from 'app-components/flux/account-discovery';
import { accountDiscoveryUtils } from 'app-components/flux/account-discovery';
import type { accountsTypes } from 'app-components/flux/accounts';
import { accountsUtils } from 'app-components/flux/accounts';

import Box from '@mui/material/Box';
import { makeStyles } from 'tss-react/mui';

import type { FormikProps } from 'formik';
import { Field, getIn } from 'formik';

import MintAccountTypeSelector from 'components/Formik/AccountTypeSelector/MintEmptyAccountType';

import FormikCheckbox from 'components/Formik/Checkbox';
import FormikTextField from 'components/Formik/TextField';

import { Typography } from '@mui/material';
import LinkActionSelector from './LinkActionSelector';

type Props = {
  // @ts-expect-error FIXME TS2694
  account: accountDiscoveryTypes.AccountData;
  accountsById: ImmutableMap<string, accountsTypes.Account>;
  index: number;

  enableLinkTo: boolean;
  stagedLinkToIds: string[];
  stagedNames?: string[];
  formik: FormikProps<any>;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    flex: 1,
    overflow: 'hidden',
    padding: 16,
    border: `1px ${theme.palette.border.light} solid`,
    borderRadius: 10,
    marginBottom: 12,
    boxShadow: theme.shadows[1],
  },
  row: {
    display: 'flex',
    flex: 1,
    gap: 16,
    '> div': {
      display: 'flex',
      flex: 1,
    },
    '.MuiFormControl-root': {
      width: '100%',
    },
  },
  checkbox: {
    paddingTop: 7,
    alignSelf: 'start',
  },
  text: {
    alignSelf: 'center',
    paddingLeft: 16,
  },
  spacer: {
    width: 40,
  },
}));

const MintAccountRowLink = (props: Props) => {
  const { account, accountsById, formik, enableLinkTo, index, stagedLinkToIds, stagedNames } = props;
  const nameRef = useRef<HTMLInputElement>(undefined);
  const { classes } = useStyles();

  const enabledFieldName = `${account.id}.enabled`;
  const nameFieldName = `${account.id}.accountName`;
  const typeFieldName = `${account.id}.type`;
  const linkToIdFieldName = `${account.id}.accountId`;

  const enabled = getIn(formik.values, enabledFieldName);
  const linkToId = getIn(formik.values, linkToIdFieldName);

  const accounts = accountsById.valueSeq().toList();

  // @ts-expect-error FIXME TS7006
  const handleLinkToChange = (event) => {
    const newLinkTo = event.target.value;
    const values = accountDiscoveryUtils.getNewAccountFieldsMint(account, newLinkTo, accountsById);
    formik.setFieldValue(nameFieldName, values.name);
    formik.setFieldValue(typeFieldName, values.type);
  };

  // @ts-expect-error FIXME TS7006
  const validateName = (stagedName) => {
    if (!enabled) return null;
    let ret: string | null = null;

    const lowerCaseName = `${stagedName?.toLowerCase()}(imported)`;
    if (!lowerCaseName) {
      return 'Account name required';
    }

    if (accountsUtils.isInvalidName(stagedName)) {
      ret = 'Invalid account name.';
    }
    // Check that name is unique across all other accounts. Note: There is a corner case that will give a false
    // error (changing two account names while linking to each other).
    //
    if (accounts) {
      accounts.forEach((otherAccount: any) => {
        if (linkToId !== otherAccount.id && otherAccount.name.toLowerCase() === lowerCaseName) {
          ret = 'Account name must be unique.';
        }
      });
    }

    // Check that two staged-names are not the same.
    let count = 0;
    stagedNames?.forEach((otherName) => {
      if (otherName === lowerCaseName || stagedName?.toLowerCase() === otherName) {
        count += 1;
      }
    });
    if (count > 1) {
      ret = 'Account name must be unique.';
    }
    return ret;
  };

  // @ts-expect-error FIXME TS7006
  const validateType = (type) => {
    if (!enabled) return null;
    let ret: string | null = null;
    if (type === undefined) {
      ret = 'Please pick an account type';
    } else if (type.qcsType === 'UNKNOWN') {
      ret = 'Please pick an account type';
    }
    return ret;
  };

  // Check that staged link-to IDs are unique (cannot link more than once to each account)
  // @ts-expect-error FIXME TS7006
  const validateLinkToId = (stagedLinkToId) => {
    if (!enabled || stagedLinkToId === '0') return null;

    let count = 0;
    stagedLinkToIds?.forEach((otherLinkToId) => {
      if (stagedLinkToId === otherLinkToId) {
        count += 1;
      }
    });
    return count > 1 ? 'Cannot link to same account' : null;
  };

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <Field component={FormikCheckbox} name={enabledFieldName} disableRipple className={classes.checkbox} />
        <Box
        // don't remove this div - it is important for catching the tooltip ref, as field
        // is a 3rd party functional component and therefore cannot hold a ref
        /* eslint-disable-next-line react/jsx-closing-bracket-location */
        >
          <Field
            component={FormikTextField}
            disabled={!enabled}
            id={`account-${index}-name`}
            name={nameFieldName}
            variant='outlined'
            validate={validateName}
            InputProps={{
              id: `account#-${index}-name`,
              ref: nameRef,
              sx: { typography: 'body2' },
              variant: 'ellipsis',
            }}
            size='small'
            sx={{ width: '100%' }}
          />
        </Box>
        <MintAccountTypeSelector
          accId={account.id}
          currentAccountType={account.type}
          name={typeFieldName}
          showManualTypes={false}
          size='small'
          validate={validateType}
          TextFieldProps={{
            disabled: !enabled || linkToId !== '0',
            id: `account-${index}-type`,
            label: null,
            variant: 'outlined',
          }}
        />
      </div>

      <div className={classes.row}>
        <span className={classes.spacer}></span>
        <div>
          <Typography variant='captionRegular' className={classes.text}>
            {account.txnCount} transactions
          </Typography>
        </div>
        {enableLinkTo && accountsById.size !== 0 && (
          <LinkActionSelector
            name={linkToIdFieldName}
            accountsById={accountsById}
            disabled={!enabled}
            linkableAccountIdsSection1={account.linkableAccountIdsSection1}
            linkableAccountIdsSection2={account.linkableAccountIdsSection2}
            linkableAccountIdsSection3={account.linkableAccountIdsSection3}
            onBlur={formik.handleBlur}
            onChange={handleLinkToChange}
            variant='outlined'
            validate={validateLinkToId}
            isMint
          />
        )}
      </div>
    </div>
  );
};

export default memo(MintAccountRowLink);
